import { useCallback, useMemo } from "react";
import { IBudgetItem, IBudgetService, IBudgetSummary } from "../types";
import {
  createEmptyService,
  createEmptyExternalService,
  createEmptyItem,
} from "../utils/mockData";
import {
  fetchBudgetItemsByServiceAndFailureMode,
  fetchBudgetServiceItemById,
} from "../../../../api/fetchBudget";

interface IUseBudgetServicesParams {
  services: IBudgetService[];
  setServices: React.Dispatch<React.SetStateAction<IBudgetService[]>>;
  occurrenceId: string;
  isEditMode: boolean;
}

export const useBudgetServices = ({
  services,
  setServices,
  occurrenceId,
  isEditMode,
}: IUseBudgetServicesParams) => {
  const calculateItemSubtotal = useCallback(
    (
      itemValue: number,
      quantity: number,
      itemDiscountPercent: number
    ): number => {
      if (quantity <= 0) return 0;

      const grossValue = itemValue * quantity;
      const itemDiscount = (grossValue * itemDiscountPercent) / 100;

      return grossValue - itemDiscount;
    },
    []
  );

  const recalculateServiceItemsSubtotal = useCallback(
    (service: IBudgetService): IBudgetItem[] => {
      return service.items.map((item) => {
        const itemValue = item.value || 0;
        let clean = String(item.quantity || "0").replace(/[^\d.,]/g, "");
        clean = clean.replace(",", ".");
        const quantityParsed = Number.parseFloat(clean);
        const quantity = Number.isNaN(quantityParsed) ? 0 : quantityParsed;

        const discountStr = String(item.discount || "0%");
        let cleanDiscountStr = discountStr.replaceAll(/[^\d.,]/g, "");
        cleanDiscountStr = cleanDiscountStr.replaceAll(",", ".");
        const discountPercent = Number.parseFloat(cleanDiscountStr) || 0;

        const subtotal = calculateItemSubtotal(
          itemValue,
          quantity,
          discountPercent
        );

        return { ...item, subtotal };
      });
    },
    [calculateItemSubtotal]
  );

  const handleAddNewService = useCallback(() => {
    const newService = createEmptyService();
    setServices((prev) => [...prev, newService]);
  }, [setServices]);

  const handleAddExternalService = useCallback(() => {
    setServices((prev) => {
      const serviceIndex = prev.length;
      const newRef = `${serviceIndex + 1}.01`;
      const newItem = createEmptyItem(newRef);
      const newService = {
        ...createEmptyExternalService(),
        items: [newItem],
      };
      return [...prev, newService];
    });
  }, [setServices]);

  const handleServiceChange = useCallback(
    async (
      serviceIndex: number,
      serviceId: string,
      serviceData?: { id: string; name: string; serviceItems: any[] }
    ) => {
      let currentFailureModeId: string | undefined;

      setServices((prev) => {
        const updatedServices = [...prev];
        const currentService = updatedServices[serviceIndex];
        currentFailureModeId = currentService?.failureModeId;
        const previousServiceId = currentService?.serviceId;

        const isServiceChanged =
          previousServiceId && previousServiceId !== serviceId;

        updatedServices[serviceIndex] = {
          ...updatedServices[serviceIndex],
          serviceId,
          serviceName: serviceData?.name || serviceId,
          items: isServiceChanged ? [] : updatedServices[serviceIndex].items,
        };
        return updatedServices;
      });

      if (
        serviceData &&
        serviceData.serviceItems &&
        serviceData.serviceItems.length > 0
      ) {
        const itemIds = serviceData.serviceItems
          .filter((e) => e.description)
          .map((e) => e.item);
        const itemsResponse = await Promise.all(
          itemIds.map(async (id) => fetchBudgetServiceItemById(id))
        );
        const items = itemsResponse.map((item) => ({
          id: item?.item || "",
          code:  item?.item || "",
          value: item?.value || 0,
          ref: item?.ref || "",
        }));

        setServices((prev) => {
          const updatedServices = [...prev];
          const serviceIndexToUpdate = updatedServices.findIndex(
            (s, idx) => idx === serviceIndex
          );
          if (serviceIndexToUpdate !== -1) {
            const filteredItems = serviceData.serviceItems.filter(
              (serviceItem) => serviceItem.description
            );
            const mappedItems = filteredItems.map((serviceItem, index) => {
              const item = items.find((item) => item.ref == serviceItem.ref);
              
              const code = item?.code || "";
              const value = serviceItem.value || item?.value || 0;
              const quantity = serviceItem.quantity ?? 1;
              const discountPercent = serviceItem.discount ?? 0;
              const calculatedSubtotal = calculateItemSubtotal(
                value,
                quantity,
                discountPercent
              );

              return {
                ref: `${serviceIndex + 1}.${String(index + 1).padStart(
                  2,
                  "0"
                )}`,
                item: code,
                description: serviceItem.description || "",
                quantity: String(quantity).padStart(2, "0"),
                value: value,
                discount: `${discountPercent}%`,
                subtotal: calculatedSubtotal,
              };
            });

            updatedServices[serviceIndexToUpdate] = {
              ...updatedServices[serviceIndexToUpdate],
              items: mappedItems,
            };
          }
          return updatedServices;
        });
      } else if (currentFailureModeId) {
        try {
          const items = await fetchBudgetItemsByServiceAndFailureMode(
            serviceId,
            currentFailureModeId
          );

          if (items.length > 0) {
            setServices((prev) => {
              const updatedServices = [...prev];
              const serviceIndexToUpdate = updatedServices.findIndex(
                (s, idx) => idx === serviceIndex
              );
              if (serviceIndexToUpdate !== -1) {
                const updatedItems = items.map((item, index) => {
                  const itemValue = item.value || 0;
                  let clean = String(item.quantity || "0").replace(
                    /[^\d.,]/g,
                    ""
                  );
                  clean = clean.replace(",", ".");
                  const quantityParsed = Number.parseFloat(clean);
                  const quantity = Number.isNaN(quantityParsed)
                    ? 0
                    : quantityParsed;

                  const discountStr = String(item.discount || "0%");
                  let cleanDiscountStr = discountStr.replaceAll(/[^\d.,]/g, "");
                  cleanDiscountStr = cleanDiscountStr.replaceAll(",", ".");
                  const discountPercent =
                    Number.parseFloat(cleanDiscountStr) || 0;

                  const subtotal = calculateItemSubtotal(
                    itemValue,
                    quantity,
                    discountPercent
                  );

                  return {
                    ...item,
                    ref: `${serviceIndex + 1}.${String(index + 1).padStart(
                      2,
                      "0"
                    )}`,
                    subtotal,
                  };
                });
                updatedServices[serviceIndexToUpdate] = {
                  ...updatedServices[serviceIndexToUpdate],
                  items: updatedItems,
                };
              }
              return updatedServices;
            });
          }
        } catch (error) {}
      } else {
        setServices((prev) => {
          const updatedServices = [...prev];
          const serviceIndexToUpdate = updatedServices.findIndex(
            (s, idx) => idx === serviceIndex
          );
          if (serviceIndexToUpdate !== -1) {
            updatedServices[serviceIndexToUpdate] = {
              ...updatedServices[serviceIndexToUpdate],
              items: [],
            };
          }
          return updatedServices;
        });
      }
    },
    [occurrenceId, calculateItemSubtotal, setServices]
  );

  const handleFailureModeChange = useCallback(
    async (serviceIndex: number, failureModeId: string) => {
      let currentServiceId: string | undefined;

      setServices((prev) => {
        const updatedServices = [...prev];
        const currentService = updatedServices[serviceIndex];
        if (!currentService || !failureModeId) {
          return prev;
        }
        currentServiceId = currentService.serviceId;
        updatedServices[serviceIndex] = {
          ...updatedServices[serviceIndex],
          failureModeId,
        };
        return updatedServices;
      });

      if (!currentServiceId || !failureModeId) {
        return;
      }

      try {
        const items = await fetchBudgetItemsByServiceAndFailureMode(
          currentServiceId,
          failureModeId
        );

        if (items.length > 0) {
          setServices((prev) => {
            const updatedServices = [...prev];
            const serviceIndexToUpdate = updatedServices.findIndex(
              (s, idx) => idx === serviceIndex
            );
            if (serviceIndexToUpdate !== -1) {
              const currentService = updatedServices[serviceIndexToUpdate];
              const updatedItems = items.map((item, index) => {
                const discountValue = item.discount || "0";
                const discountStr = String(discountValue);
                const discountFormatted = discountStr.includes("%")
                  ? discountStr
                  : `${discountStr}%`;

                const itemValue = item.value ?? 0;
                let clean = String(item.quantity || "0").replace(
                  /[^\d.,]/g,
                  ""
                );
                clean = clean.replace(",", ".");
                const quantityParsed = Number.parseFloat(clean);
                const quantity = Number.isNaN(quantityParsed)
                  ? 0
                  : quantityParsed;

                let cleanDiscountStr = discountFormatted.replaceAll(
                  /[^\d.,]/g,
                  ""
                );
                cleanDiscountStr = cleanDiscountStr.replaceAll(",", ".");
                const discountPercent =
                  Number.parseFloat(cleanDiscountStr) || 0;

                const subtotal = calculateItemSubtotal(
                  itemValue,
                  quantity,
                  discountPercent
                );

                const mappedItem = {
                  ...item,
                  ref: `${serviceIndex + 1}.${String(index + 1).padStart(
                    2,
                    "0"
                  )}`,
                  item: item.item || item.ref || "",
                  quantity: item.quantity
                    ? String(item.quantity).padStart(2, "0")
                    : "01",
                  value: itemValue,
                  discount: discountFormatted,
                  subtotal,
                };
                return mappedItem;
              });
              updatedServices[serviceIndexToUpdate] = {
                ...updatedServices[serviceIndexToUpdate],
                items: updatedItems,
              };
            }
            return updatedServices;
          });
        }
      } catch (error) {
        // Error handled silently
      }
    },
    [occurrenceId, calculateItemSubtotal, setServices]
  );

  const handleItemChange = useCallback(
    (
      serviceIndex: number,
      itemIndex: number,
      field: keyof IBudgetItem,
      value: string | number
    ) => {
      setServices((prev) => {
        const updatedServices = [...prev];
        const updatedItems = [...updatedServices[serviceIndex].items];
        const currentItem = updatedItems[itemIndex];

        const updatedItem = { ...currentItem, [field]: value };
        updatedItems[itemIndex] = updatedItem;

        if (field === "value" || field === "quantity" || field === "discount") {
          const itemValue =
            field === "value" ? (value as number) : updatedItem.value || 0;
          let quantityParsed: number;
          if (field === "quantity") {
            let clean = String(value).replace(/[^\d.,]/g, "");
            clean = clean.replace(",", ".");
            quantityParsed = Number.parseFloat(clean);
          } else {
            let clean = String(updatedItem.quantity || "1").replace(
              /[^\d.,]/g,
              ""
            );
            clean = clean.replace(",", ".");
            quantityParsed = Number.parseFloat(clean);
          }
          const quantity = Number.isNaN(quantityParsed) ? 0 : quantityParsed;

          let discountStr: string;
          if (field === "discount") {
            if (typeof value === "number") {
              discountStr = `${value}%`;
            } else {
              discountStr = String(value);
            }
          } else {
            discountStr = String(updatedItem.discount || "0%");
          }

          let cleanDiscountStr = discountStr.replaceAll(/[^\d.,]/g, "");
          cleanDiscountStr = cleanDiscountStr.replaceAll(",", ".");
          const discountPercent = Number.parseFloat(cleanDiscountStr) || 0;

          const subtotal =
            quantity > 0
              ? calculateItemSubtotal(itemValue, quantity, discountPercent)
              : 0;

          updatedItems[itemIndex] = { ...updatedItems[itemIndex], subtotal };
        }

        updatedServices[serviceIndex] = {
          ...updatedServices[serviceIndex],
          items: updatedItems,
        };
        return updatedServices;
      });
    },
    [calculateItemSubtotal, setServices]
  );

  const handleAddItem = useCallback(
    (serviceIndex: number) => {
      setServices((prev) => {
        const updatedServices = [...prev];
        const currentService = updatedServices[serviceIndex];
        const currentItems = currentService.items || [];
        const nextItemIndex = currentItems.length + 1;
        const newRef = `${serviceIndex + 1}.${String(nextItemIndex).padStart(
          2,
          "0"
        )}`;
        const newItem = createEmptyItem(newRef);

        const subtotal = calculateItemSubtotal(newItem.value || 0, 1, 0);
        const itemWithSubtotal = { ...newItem, subtotal };

        updatedServices[serviceIndex] = {
          ...updatedServices[serviceIndex],
          items: [...currentItems, itemWithSubtotal],
        };
        return updatedServices;
      });
    },
    [calculateItemSubtotal, setServices]
  );

  const handleRemoveItem = useCallback(
    (serviceIndex: number, itemIndex: number) => {
      setServices((prev) => {
        const updatedServices = [...prev];
        const updatedItems = [...updatedServices[serviceIndex].items];

        if (isEditMode) {
          updatedItems[itemIndex] = {
            ...updatedItems[itemIndex],
            removed: true,
            quantity: "00",
            subtotal: 0,
          };
        } else {
          updatedItems.splice(itemIndex, 1);
        }

        updatedServices[serviceIndex] = {
          ...updatedServices[serviceIndex],
          items: updatedItems,
        };
        return updatedServices;
      });
    },
    [isEditMode, setServices]
  );

  const handleAddObservation = useCallback(
    (serviceIndex: number) => {
      setServices((prev) => {
        const updatedServices = [...prev];
        updatedServices[serviceIndex] = {
          ...updatedServices[serviceIndex],
          observation: "",
        };
        return updatedServices;
      });
    },
    [setServices]
  );

  const handleObservationChange = useCallback(
    (serviceIndex: number, observation: string) => {
      setServices((prev) => {
        const updatedServices = [...prev];
        updatedServices[serviceIndex] = {
          ...updatedServices[serviceIndex],
          observation,
        };
        return updatedServices;
      });
    },
    [setServices]
  );

  const handleRemoveObservation = useCallback(
    (serviceIndex: number) => {
      setServices((prev) => {
        const updatedServices = [...prev];
        updatedServices[serviceIndex] = {
          ...updatedServices[serviceIndex],
          observation: undefined,
        };
        return updatedServices;
      });
    },
    [setServices]
  );

  const handleVolkstotalPercentageChange = useCallback(
    (serviceIndex: number, value: number) => {
      setServices((prev) => {
        const updatedServices = [...prev];
        const currentService = updatedServices[serviceIndex];
        const dealership = currentService.dealershipPercentage || 0;
        const warranty = currentService.warrantyPercentage || 0;

        const maxValue = Math.max(0, 100 - (dealership + warranty));
        const limitedValue = Math.min(value, maxValue);
        const clientPercentage = Math.max(
          0,
          100 - (limitedValue + dealership + warranty)
        );

        const updatedService = {
          ...updatedServices[serviceIndex],
          volkstotalPercentage: limitedValue,
          clientPercentage,
        };
        updatedServices[serviceIndex] = {
          ...updatedService,
          items: recalculateServiceItemsSubtotal(updatedService),
        };
        return updatedServices;
      });
    },
    [recalculateServiceItemsSubtotal, setServices]
  );

  const handleDealershipPercentageChange = useCallback(
    (serviceIndex: number, value: number) => {
      setServices((prev) => {
        const updatedServices = [...prev];
        const currentService = updatedServices[serviceIndex];
        const volkstotal = currentService.volkstotalPercentage || 0;
        const warranty = currentService.warrantyPercentage || 0;

        const maxValue = Math.max(0, 100 - (volkstotal + warranty));
        const limitedValue = Math.min(value, maxValue);
        const clientPercentage = Math.max(
          0,
          100 - (volkstotal + limitedValue + warranty)
        );

        const updatedService = {
          ...updatedServices[serviceIndex],
          dealershipPercentage: limitedValue,
          clientPercentage,
        };
        updatedServices[serviceIndex] = {
          ...updatedService,
          items: recalculateServiceItemsSubtotal(updatedService),
        };
        return updatedServices;
      });
    },
    [recalculateServiceItemsSubtotal, setServices]
  );

  const handleWarrantyPercentageChange = useCallback(
    (serviceIndex: number, value: number) => {
      setServices((prev) => {
        const updatedServices = [...prev];
        const currentService = updatedServices[serviceIndex];
        const volkstotal = currentService.volkstotalPercentage || 0;
        const dealership = currentService.dealershipPercentage || 0;

        const maxValue = Math.max(0, 100 - (volkstotal + dealership));
        const limitedValue = Math.min(value, maxValue);
        const clientPercentage = Math.max(
          0,
          100 - (volkstotal + dealership + limitedValue)
        );

        const updatedService = {
          ...updatedServices[serviceIndex],
          warrantyPercentage: limitedValue,
          clientPercentage,
        };
        updatedServices[serviceIndex] = {
          ...updatedService,
          items: recalculateServiceItemsSubtotal(updatedService),
        };
        return updatedServices;
      });
    },
    [recalculateServiceItemsSubtotal, setServices]
  );

  const handleRemoveService = useCallback(
    (serviceIndex: number) => {
      setServices((prev) => {
        return prev.filter((_, index) => index !== serviceIndex);
      });
    },
    [setServices]
  );

  const calculatedSummary = useMemo((): IBudgetSummary => {
    let totalSubtotal = 0;
    let totalDiscounts = 0;

    for (const service of services) {
      let serviceSubtotal = 0;

      for (const item of service.items) {
        if (item.removed) continue;

        const itemValue = item.value || 0;
        let clean = String(item.quantity || "0").replace(/[^\d.,]/g, "");
        clean = clean.replace(",", ".");
        const parsedQuantity = Number.parseFloat(clean);
        const quantity = Number.isNaN(parsedQuantity) ? 0 : parsedQuantity;

        if (quantity <= 0) continue;

        const discountStr = String(item.discount || "0%");
        let cleanDiscountStr = discountStr.replaceAll(/[^\d.,]/g, "");
        cleanDiscountStr = cleanDiscountStr.replaceAll(",", ".");
        const itemDiscountPercent = Number.parseFloat(cleanDiscountStr) || 0;

        const grossValue = itemValue * quantity;
        const itemDiscount = (grossValue * itemDiscountPercent) / 100;
        const itemSubtotal = grossValue - itemDiscount;

        serviceSubtotal += itemSubtotal;
      }

      const serviceDiscountPercent =
        (service.volkstotalPercentage || 0) +
        (service.dealershipPercentage || 0) +
        (service.warrantyPercentage || 0);

      const serviceDiscount = (serviceSubtotal * serviceDiscountPercent) / 100;

      totalSubtotal += serviceSubtotal;
      totalDiscounts += serviceDiscount;
    }

    const totalToPay = totalSubtotal - totalDiscounts;

    return {
      subtotal: totalSubtotal,
      discounts: totalDiscounts,
      totalToPay: totalToPay,
    };
  }, [services]);

  const hasItemsWithZeroQuantity = useMemo(() => {
    for (const service of services) {
      for (const item of service.items) {
        if (item.removed) continue;

        const quantityStr = String(item.quantity || "").trim();
        if (
          !quantityStr ||
          quantityStr === "" ||
          quantityStr === "0" ||
          quantityStr === "00"
        ) {
          return true;
        }

        let clean = quantityStr.replace(/[^\d.,]/g, "");
        clean = clean.replace(",", ".");
        const parsedQuantity = Number.parseFloat(clean);

        if (Number.isNaN(parsedQuantity) || parsedQuantity <= 0) {
          return true;
        }

        const itemValue = item.value || 0;
        if (itemValue <= 0) {
          return true;
        }
      }
    }
    return false;
  }, [services]);

  return {
    calculateItemSubtotal,
    handleAddNewService,
    handleAddExternalService,
    handleServiceChange,
    handleFailureModeChange,
    handleItemChange,
    handleAddItem,
    handleRemoveItem,
    handleAddObservation,
    handleObservationChange,
    handleRemoveObservation,
    handleVolkstotalPercentageChange,
    handleDealershipPercentageChange,
    handleWarrantyPercentageChange,
    handleRemoveService,
    calculatedSummary,
    hasItemsWithZeroQuantity,
  };
};
